import React, { useEffect } from 'react';
import { Row, Col, Progress, Divider, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import './MemberShipSection.scss';
import AnimatedNumber from 'components/animatedNumber/AnimatedNumber';
import CreditCard from 'components/creditCard/CreditCard';
import { CreditCardTier, MembershipTier } from 'types/dto/profile';
import { SLASH_FORMAT } from 'constants/consts';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { buildBenefitsPath } from 'utils/pathBuilder';

export interface MemberShipSectionProps {
  membershipData: MembershipTier;
  isVertical?: boolean;
}

const MemberShipSection: React.FC<MemberShipSectionProps> = ({
  membershipData,
  isVertical = false,
}) => {
  const { t, i18n } = useTranslation('profile');
  const lang = i18n.language as 'en' | 'zh';
  const navigate = useNavigate();

  const ANIMATION_DURATION = 500; // 0.5s
  const [progressPercentage, setProgressPercentage] = React.useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgressPercentage(membershipData.progress?.percentage ?? 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [membershipData.progress?.percentage]);

  const handleLevelCheck = () => {
    navigate(buildBenefitsPath());
  };

  const currentTierCode =
    membershipData.current_tier.code.toLowerCase().replace('tier-', '') as CreditCardTier;
  const currentTierColor = membershipData.current_tier.color;
  const hasNextTier = !!membershipData.next_tier?.code;

  return (
    <div className={`membership-section ${isVertical ? 'is-vertical' : ''}`}>
      <Row gutter={[48, 24]}>
        <Col xs={24} sm={isVertical ? 24 : 12} className="card-col">
          <div className="membership-card">
            <CreditCard
              tier={currentTierCode}
              tierTitle={membershipData.current_tier.title[lang]}
              validThru={dayjs(membershipData.expire_at).format(
                SLASH_FORMAT
              )}
              height={isVertical ? '110px' : ''}
            />
          </div>
        </Col>
        <Col xs={24} sm={isVertical ? 24 : 12}>
          <div className="progress-card">
            <div className="progress-content">
              <div className="progress-circle">
                <Progress
                  type="circle"
                  percent={progressPercentage}
                  format={() => (
                    <div className="progress-text">
                      <div className="amount">
                        <AnimatedNumber
                          value={membershipData.progress?.current ?? 0}
                          prefix="$"
                          duration={ANIMATION_DURATION}
                          easingFunction="ease-out"
                          color={currentTierColor}
                        />
                      </div>

                      {hasNextTier && (
                        <>
                          <Divider className="divider" />
                          <div className="amount">
                            <AnimatedNumber
                              value={membershipData.progress?.total ?? 0}
                              prefix="$"
                              duration={ANIMATION_DURATION}
                              easingFunction="ease-out"
                              color={currentTierColor}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  strokeColor={currentTierColor}
                  size={isVertical ? 130 : 160}
                  strokeWidth={12}
                />
              </div>
              <div className="upgrade-text">
                {hasNextTier ? (
                  <>
                    {t('membershipCard.spendMore', {
                      amount:
                        (membershipData.progress?.total ?? 0) -
                        (membershipData.progress?.current ?? 0),
                      date: dayjs(membershipData.expire_at).format(
                        'D MMM YYYY'
                      ),
                    })}
                    <Button
                      type="link"
                      onClick={() => handleLevelCheck()}
                      className="level-button"
                    >
                      {membershipData.next_tier?.title[lang]}
                    </Button>
                  </>
                ) : (
                  t('membershipCard.topTierReached')
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default MemberShipSection;
